import React, { useState, useRef, useCallback } from "react";
import {
  Button,
  CircularProgress,
} from "@mui/material";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import { Radio, RadioGroup, FormControl } from "@mui/material";
import Navbar from "../../../globals/components/Navbar";
import { useEffect } from "react";
import { apiClient } from "../../../globals/utils/api";
import CreatableSelect from "react-select/creatable";
import { FixedSizeList as List } from "react-window";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { NumericFormat } from "react-number-format";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import { calculatePremium } from "../../../globals/utils/commons";
import { BillingInformationForm } from "./Helpers/BillingInformation";
import { ObligeeInformationForm } from "./Helpers/ObligeeInformation";
import { is_prod } from "../../../globals/utils/constants";
import { loadGoogleMaps } from "../../../globals/utils/googleMapsLoader";
import { BondNumberSelect } from "../Bond Creation Historic/Helpers/BondNumberSelect";
import { extractErrorMessage } from "../../../globals/utils/error-handler";
import { VA_FIELD_LIMITS, validateFieldLengths } from "../../../globals/utils/bondFieldLimits";
import SupportRequestModal from "../../../components/SupportRequestModal";
import { createSupportRequest } from "../../../services/supportRequestService";

// Custom dropdown styling (matches MUI styles)
const customStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "transparent", // Make the background transparent
    minHeight: "56px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    boxShadow: "none",
    fontSize: "16px", // Decrease font size
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999, // Increase z-index for dropdown menu
    fontSize: "16px", // Decrease font size
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999, // Increase z-index for menu portal
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#6200ea"
      : state.isFocused
      ? "#f0f0f0"
      : "#fff",
    color: state.isSelected ? "#fff" : "#000",
    fontSize: "16px", // Decrease font size
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  }),
};

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
      fixedDecimalScale
    />
  );
});

// Labeled CreatableSelect Component
const LabeledCreatableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder,
}) => {
  return (
    <Box sx={{ marginBottom: "2px" }}>
      {/* Label styled to mimic MUI's TextField label */}
      <Typography
        sx={{
          fontSize: "12px",
          color: "rgba(0, 0, 0, 0.6)",
          marginBottom: "4px",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <CreatableSelect
        isClearable
        options={options}
        components={{ MenuList }}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        styles={customStyles}
      />
    </Box>
  );
};

// Virtualized MenuList for better performance
const MenuList = (props) => {
  const height = 35;
  const { options, children, maxHeight } = props;

  return (
    <List
      height={Math.min(maxHeight, options.length * height)}
      itemCount={children.length}
      itemSize={height}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
};

// Google Autocomplete Component
const GoogleAutoComplete = ({ placeholder, callback }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        { fields: ["address_components"], types: ["geocode"] }
      );

      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();

        if (!place || !place.address_components) {
          console.error(
            "No valid place returned or missing address components."
          );
          return;
        }

        let address = "",
          city = "",
          state = "",
          zipCode = "";

        place.address_components.forEach((component) => {
          const type = component.types[0];
          switch (type) {
            case "street_number":
              address = `${component.long_name} ${address}`;
              break;
            case "route":
              address += component.long_name;
              break;
            case "locality":
              city = component.long_name;
              break;
            case "administrative_area_level_1":
              state = component.long_name;
              break;
            case "postal_code":
              zipCode = component.long_name;
              break;
            default:
              break;
          }
        });

        if (callback) {
          callback(address, city, state, zipCode);
        }
      });
    }
  }, [loaded, callback]);

  return (
    <TextField
      variant="outlined"
      fullWidth
      placeholder={placeholder}
      inputRef={inputRef}
      sx={{ marginBottom: "8px" }} // Add margin-bottom
    />
  );
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth", // Smooth scrolling
  });
};

const BasicInformationForm = ({ globalData, data, updateData, errors }) => {
  // Handle input change and directly update the global state
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "bondAmount") {
      const updatedData = { ...data, [name]: value };
      updateData(updatedData);

      if (updatedData.bondAmount) {
        const bondAmount = parseFloat(
          updatedData.bondAmount.replace(/[^0-9]/g, "")
        );
        if (!isNaN(bondAmount)) {
          const premium = calculatePremium(bondAmount);
          updateData({
            ...updatedData,
            premiumAmount: premium,
            PrincipalNumber: "1",
          });
        }
      }
    } else {
      updateData({ ...data, [name]: value });
    }
  };

  const handleAutocompleteChange = (event, value, fieldName) => {
    updateData({ ...data, [fieldName]: value });
  };

  // Fetch state and bondType from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem("selectedState");
    const savedType = localStorage.getItem("selectedBondType");

    if (savedState) {
      const parsedState = JSON.parse(savedState);
      updateData({ ...data, state: parsedState.label }); // Add state to data
    }

    if (savedType) {
      const parsedType = JSON.parse(savedType);
      updateData({ ...data, bondType: parsedType.label }); // Add bondType to data
    }

    scrollToTop(); // Assuming this is a function to scroll to the top
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format numbers as US currency
  const formatAsCurrency = (value) => {
    if (!value) return "";
    const numericValue = parseFloat(value);
    return numericValue.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Basic Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        <TextField
          label="Beneficiary Legal First Name"
          name="firstName"
          value={data.firstName || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.firstName}
          helperText={errors.firstName}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          label="Beneficiary Legal Middle Name"
          name="middleName"
          value={data.middleName || ""}
          onChange={handleChange}
          fullWidth
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          label="Beneficiary Legal Last Name"
          name="lastName"
          value={data.lastName || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.lastName}
          helperText={errors.lastName}
          inputProps={{ maxLength: 200 }}
        />
        <Autocomplete
          options={["Jr", "Sr", "I", "II", "III", "IV"]}
          value={data.suffix || ""}
          onChange={(event, value) =>
            handleAutocompleteChange(event, value, "suffix")
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Suffix"
              fullWidth
              variant="outlined"
            />
          )}
        />
        <TextField
          label="Aliases"
          name="aliases"
          value={data.aliases || ""}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          label="Date of Birth"
          name="dateOfBirth"
          value={data.dateOfBirth || ""}
          onChange={handleChange}
          type="date"
          fullWidth
          required
          error={!!errors.dateOfBirth}
          helperText={errors.dateOfBirth}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Bond Amount"
          name="bondAmount"
          value={data.bondAmount || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.bondAmount}
          helperText={errors.bondAmount}
          InputProps={{
            inputComponent: NumberFormatCustom,
          }}
        />
        <TextField
          label="Premium Amount"
          name="premiumAmount"
          value={formatAsCurrency(data.premiumAmount)}
          fullWidth
          required
          disabled
        />
        <TextField
          select
          label="Do you have an official court case number?"
          name="hasCourtCaseNumber"
          value={data.hasCourtCaseNumber || ""}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "No") {
              updateData({ ...data, hasCourtCaseNumber: "No", courtCaseNumber: "" });
            } else {
              updateData({ ...data, hasCourtCaseNumber: val });
            }
          }}
          fullWidth
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.hasCourtCaseNumber === "Yes" && (
          <TextField
            label="VA File #"
            name="courtCaseNumber"
            value={data.courtCaseNumber || ""}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.courtCaseNumber}
            helperText={errors.courtCaseNumber}
            inputProps={{ maxLength: 30 }}
          />
        )}
        <TextField
          label="Number Of Principals"
          name="PrincipalNumber"
          value={data.PrincipalNumber || 1}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.PrincipalNumber}
          helperText={errors.PrincipalNumber}
          type="number"
          InputProps={{
            inputProps: {
              min: 1,
              max: 4,
            },
          }}
        />
      </Box>
    </Box>
  );
};

const GuardianForm = ({
  data = { guardians: [] },
  updateData,
  globalData,
  errors,
}) => {
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [professionalGuardians, setProfessionalGuardians] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);
  const [loadingGuardians, setLoadingGuardians] = useState(false);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);
  const { role } = useSelector((state) => state.auth);
  const pgId = useSelector((state) => state.pg.pgId);
  const lawfirm = useSelector((state) => state.lawfirm);

  // Ref to track current data for async functions to avoid stale closures
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (!role && pgId) {
      fetchProfessionalGuardianDetails(0, pgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, pgId, professionalGuardians]);

  // Fetch cities and states for dropdowns
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        setAllCities(cities);
        setAllStates(states);
        scrollToTop();
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };
    loadDropdownData();
  }, []);

  // Fetch Professional Guardians from API
  useEffect(() => {
    const fetchProfessionalGuardians = async () => {
      setLoadingGuardians(true);
      try {
        const response = await apiClient(
          "/get-all-professional-guardians",
          "GET"
        );
        const formattedGuardians = response.data.map((guardian) => ({
          value: guardian.pg_id,
          label: guardian.full_name,
          address:
            guardian.mailing_address === ", , ,"
              ? guardian.physical_address
              : guardian.mailing_address,
          cell: guardian.cell_phone,
        }));
        setProfessionalGuardians(formattedGuardians);
      } catch (error) {
        console.error("Error fetching professional guardians:", error);
      } finally {
        setLoadingGuardians(false);
      }
    };

    fetchProfessionalGuardians();
  }, []);

  // Fetch Attorneys from API
  useEffect(() => {
    const fetchAttorneys = async () => {
      setLoadingAttorneys(true);
      let id = 0;
      if (role === "law_firm_user" || role === "law_firm_admin") {
        id = lawfirm.lawfirmId;
      }
      try {
        const response = await apiClient(`/lawfirm-attorneys/${id}`, "GET");
        const formattedAttorneys = response.map((attorney) => ({
          value: attorney.lawfirm_user_id,
          label: attorney.full_name,
          position: attorney.position,
          address: attorney.mailing_address,
          city: attorney.city,
          state: attorney.state,
          zip_code: attorney.zip_code,
        }));
        setAttorneys(formattedAttorneys);
      } catch (error) {
        console.error("Error fetching attorneys:", error);
      } finally {
        setLoadingAttorneys(false);
      }
    };

    fetchAttorneys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleCallback = (index, address, city, state, zipCode) => {
    // Use dataRef.current to get fresh state
    const currentData = dataRef.current;
    const updatedGuardians = Array.isArray(currentData.guardians) ? [...currentData.guardians] : [];
    updatedGuardians[index] = {
      ...updatedGuardians[index],
      address: address,
      city: city,
      state: state,
      zip_code: zipCode,
    };
    updateData({ ...currentData, guardians: updatedGuardians });
  };

  //Fetch details for one professional guardians
  const fetchProfessionalGuardianDetails = async (index, id) => {
    try {
      const response = await apiClient(`/guardian-details/${id}/`, "GET");
      // Use dataRef.current to get fresh state after async call
      const currentData = dataRef.current;
      const updatedGuardians = Array.isArray(currentData.guardians)
        ? [...currentData.guardians]
        : [];
      updatedGuardians[index] = {
        ...updatedGuardians[index],
        name: response.full_name,
        address: response.mailing_address,
        city: response.city,
        state: response.state,
        zip_code: response.zip_code,
        professional_guardian: id,
        is_professional_guardian: "Yes",
        is_new_pg: false,
        showFullForm: false,
      };
      updateData({ ...currentData, guardians: updatedGuardians });
    } catch (error) {
      console.error("Error fetching professional guardian details", error);
    }
  };

  // Handle attorney selection
  const handleAttorneySelection = (index, attorney) => {
    if (attorney) {
      const updatedGuardians = [...data.guardians];
      updatedGuardians[index] = {
        ...updatedGuardians[index],
        name: attorney.label,
        address: attorney.address,
        city: attorney.city,
        state: attorney.state,
        zip_code: attorney.zip_code,
        is_attorney: "Yes",
        occupation: attorney.position,
        relationship: "Attorney of Record",
        citizenship: "USA",
        country: "USA",
        attorney: attorney.value,
        showAttorneyForm: false,
      };
      updateData({ ...data, guardians: updatedGuardians });
    }
  };

  const handleZipCodeChange = async (e, index) => {
    const zipCode = e.target.value;
    const updatedGuardians = [...data.guardians];
    updatedGuardians[index] = { ...updatedGuardians[index], zip_code: zipCode };
    updateData({ ...data, guardians: updatedGuardians });

    // If ZIP code is 5 digits, fetch city and state
    if (zipCode.length === 5) {
      try {
        const response = await apiClient(`/fields/location/${zipCode}`, "GET");
        const { city, state } = response;
        // Use dataRef.current to get fresh state after async call
        const currentData = dataRef.current;
        const freshGuardians = Array.isArray(currentData.guardians) ? [...currentData.guardians] : [];
        freshGuardians[index] = {
          ...freshGuardians[index],
          city: city,
          state: state,
          zip_code: zipCode,
        };
        updateData({ ...currentData, guardians: freshGuardians });
      } catch (error) {
        console.error("Error fetching city/state by zip code:", error);
      }
    }
  };

  const formatSSN = (ssn) => {
    if (ssn.length < 4) return ssn;
    return "XXX-XX-" + ssn.slice(-4);
  };

  const renderGuardianFields = () => {
    const guardianFields = [];
    const numberOfGuardians = globalData.basicInformation?.PrincipalNumber || 1;

    // Ensure data.guardians is always an array
    const guardians = Array.isArray(data.guardians) ? data.guardians : [];

    for (let i = 0; i < numberOfGuardians; i++) {
      // Ensure guardian is initialized as an empty object if it does not exist at index i
      const guardian = guardians[i] || {};

      guardianFields.push(
        <Box key={`guardian${i}`} className="grid grid-cols-1 gap-4">
          {/* Dynamic Heading for Professional Guardian */}
          <Typography variant="h6" className="font-semibold mb-2">
            Principal #{i + 1}
          </Typography>

          {/* Is this Guardian a Professional Guardian? */}
          <TextField
            select
            label="Is Principal a Professional Guardian?"
            value={guardian.is_professional_guardian || "No"}
            onChange={(e) => {
              const value = e.target.value;
              const updatedGuardians = [...guardians];
              updatedGuardians[i] = {
                ...updatedGuardians[i],
                is_professional_guardian: value,
                showFullForm: value === "Yes" ? guardian.showFullForm : false,
                professional_guardian: null,
                is_new_pg: false,
              };
              updateData({ ...data, guardians: updatedGuardians });
            }}
            fullWidth
            required
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </TextField>

          {/* Is this Guardian an Attorney? (only shown if not professional guardian) */}
          {!(guardian.is_professional_guardian === "Yes") && (
            <TextField
              sx={{ mb: 2 }}
              select
              label="Is Principal an Attorney?"
              value={guardian.is_attorney || "No"}
              onChange={(e) => {
                const value = e.target.value;
                const updatedGuardians = [...guardians];
                updatedGuardians[i] = {
                  ...updatedGuardians[i],
                  is_attorney: value,
                  attorney: null,
                  showAttorneyForm: false,
                };
                updateData({ ...data, guardians: updatedGuardians });
              }}
              fullWidth
              required
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          )}

          {/* Professional Guardian Autocomplete */}
          {guardian.is_professional_guardian === "Yes" &&
          !guardian.showFullForm ? (
            <Box sx={{ mt: 4 }}>
              <Autocomplete
                options={professionalGuardians}
                value={
                  professionalGuardians.find(
                    (pg) => pg.value === guardians[i].professional_guardian
                  ) || null
                }
                getOptionLabel={(option) => option.label || ""}
                loading={loadingGuardians}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Professional Guardian"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingGuardians ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      professional_guardian: newValue.value,
                      is_new_pg: false,
                    };
                    fetchProfessionalGuardianDetails(i, newValue.value);
                    updateData({ ...data, guardians: updatedGuardians });
                  }
                }}
              />
              {guardian.is_professional_guardian === "Yes" &&
                !guardian.showFullForm &&
                (() => {
                  const pg = professionalGuardians.find(
                    (pg) => pg.value === guardian.professional_guardian
                  );
                  return pg ? (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        backgroundColor: "#f9f9f9",
                        boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Selected Professional Guardian:
                      </Typography>
                      <Typography variant="body1">
                        <strong>Name:</strong> {pg.label}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Address:</strong> {pg.address}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Cell Phone:</strong> {pg.cell}
                      </Typography>
                    </Box>
                  ) : null;
                })()}
              <Typography
                variant="body2"
                className="text-blue-500 cursor-pointer mt-2"
                onClick={() => {
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    showFullForm: true,
                    is_new_pg: true,
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
              >
                Professional Guardian not in the list?
              </Typography>
            </Box>
          ) : guardian.is_attorney === "Yes" && !guardian.showAttorneyForm ? (
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                options={attorneys}
                value={
                  attorneys.find(
                    (attorney) => attorney.value === guardian.attorney
                  ) || null
                }
                getOptionLabel={(option) => option.label || ""}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue || inputValue.length < 3) return [];
                  const input = inputValue.toLowerCase();
                  return options.filter((opt) =>
                    (opt.label || "").toLowerCase().includes(input)
                  );
                }}
                noOptionsText="Type at least 3 characters to search for attorneys"
                loading={loadingAttorneys}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Attorney"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAttorneys ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(event, newValue) =>
                  handleAttorneySelection(i, newValue)
                }
              />
              {guardian.is_attorney === "Yes" &&
                guardian.attorney &&
                (() => {
                  const attorney = attorneys.find(
                    (a) => a.value === guardian.attorney
                  );
                  return attorney ? (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        backgroundColor: "#f9f9f9",
                        boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Selected Attorney:
                      </Typography>
                      <Typography variant="body1">
                        <strong>Name:</strong> {attorney.label}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Position:</strong> {attorney.position}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Address:</strong> {attorney.address}
                      </Typography>
                      <Typography variant="body1">
                        <strong>City/State/Zip:</strong> {attorney.city},{" "}
                        {attorney.state} {attorney.zip_code}
                      </Typography>
                    </Box>
                  ) : null;
                })()}
            </Box>
          ) : (
            <Box className="grid grid-cols-1 gap-4">
              {/* Name of Guardian */}
              <TextField
                sx={{ mt: 2, mb: 2 }}
                label="Name"
                value={guardian.name || ""}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    name: inputValue,
                    citizenship: "USA",
                    country: "USA",
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
                fullWidth
                required
                inputProps={{ maxLength: 255 }}
              />

              {guardian.is_new_pg && (
                <TextField
                  sx={{ mb: 2 }}
                  label="Email of Professional Guardian"
                  value={guardian.email || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      email: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                />
              )}

              {/* Citizenship */}
              <Box>
                <TextField
                  select
                  label="Citizenship"
                  value={
                    guardian.citizenship === "Other"
                      ? "Other"
                      : guardian.citizenship || "USA"
                  }
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    if (e.target.value === "Other") {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        citizenship: "Other",
                      };
                    } else {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        citizenship: e.target.value,
                      };
                    }
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                >
                  <MenuItem value="USA">USA</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="UK">UK</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="Pakistan">Pakistan</MenuItem>
                  <MenuItem value="China">China</MenuItem>
                  <MenuItem value="Other">Other (Please specify)</MenuItem>
                </TextField>

                {guardian.citizenship === "Other" && (
                  <TextField
                    label="Specify Citizenship"
                    value={guardian.custom_citizenship || ""}
                    onChange={(e) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        custom_citizenship: e.target.value,
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    fullWidth
                    required
                    placeholder="Enter citizenship"
                  />
                )}
              </Box>

              {/* Country of Residence */}
              <Box>
                <TextField
                  select
                  label="Country of Residence"
                  value={
                    guardian.country === "Other"
                      ? "Other"
                      : guardian.country || "USA"
                  }
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    if (e.target.value === "Other") {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        country: "Other",
                      };
                    } else {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        country: e.target.value,
                      };
                    }
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                >
                  <MenuItem value="USA">USA</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="UK">UK</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="Pakistan">Pakistan</MenuItem>
                  <MenuItem value="China">China</MenuItem>
                  <MenuItem value="Other">Other (Please specify)</MenuItem>
                </TextField>

                {guardian.country === "Other" && (
                  <TextField
                    label="Specify Country"
                    value={guardian.custom_country || ""}
                    onChange={(e) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        custom_country: e.target.value,
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    fullWidth
                    required
                    placeholder="Enter country"
                  />
                )}
              </Box>

              {/* Relationship to Ward */}
              {!(guardian.is_professional_guardian === "Yes") && (
                <TextField
                  label="Relationship to Beneficiary"
                  value={guardian.relationship || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      relationship: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                />
              )}

              {/* Google AutoComplete for Address */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "rgba(0, 0, 0, 0.6)",
                    marginBottom: "4px",
                    fontWeight: 500,
                  }}
                >
                  Search Address
                </Typography>
                <GoogleAutoComplete
                  placeholder="Search Address"
                  callback={(address, city, state, zipCode) =>
                    handleGoogleCallback(i, address, city, state, zipCode)
                  }
                />
              </Box>

              <TextField
                sx={{ mt: 1, mb: 1 }}
                label="Street Address"
                value={guardian.address || ""}
                onChange={(e) => {
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    address: e.target.value,
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
                fullWidth
                required
              />

              {/* ZIP Code */}
              <TextField
                label="ZIP/Postal Code"
                value={guardian.zip_code || ""}
                onChange={(e) => handleZipCodeChange(e, i)}
                fullWidth
                required
              />

              {/* City and State Dropdowns */}
              {loadingCitiesStates ? (
                <Box display="flex" justifyContent="center" my={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <LabeledCreatableSelect
                    label="City"
                    options={allCities.map((city) => ({
                      label: city,
                      value: city,
                    }))}
                    value={
                      guardian.city
                        ? { label: guardian.city, value: guardian.city }
                        : null
                    }
                    onChange={(opt) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        city: opt ? opt.value : "",
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    placeholder="Select or add a city"
                  />
                  <LabeledCreatableSelect
                    label="State"
                    options={allStates.map((state) => ({
                      label: state,
                      value: state,
                    }))}
                    value={
                      guardian.state
                        ? { label: guardian.state, value: guardian.state }
                        : null
                    }
                    onChange={(opt) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        state: opt ? opt.value : "",
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    placeholder="Select or add a state"
                  />
                </>
              )}

              {/* SSN and Occupation Fields */}
              {globalData.basicInformation?.bondAmount > 250000 && (
                <TextField
                  label="Social Security Number (SSN)"
                  type="password"
                  value={guardian.ssn || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      ssn: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                  helperText={formatSSN(guardian.ssn || "")}
                />
              )}

              {/* {globalData.basicInformation?.bondAmount > 100000 && !(guardian.is_professional_guardian === "Yes") && (
                <TextField
                  label="Occupation"
                  value={guardian.occupation || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = { ...updatedGuardians[i], occupation: e.target.value };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                />
              )} */}
            </Box>
          )}
        </Box>
      );
    }

    return guardianFields;
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Principals
      </Typography>

      {/* Render guardian fields */}
      {renderGuardianFields()}
    </Box>
  );
};

const AttorneyInformationForm = ({ data, updateData, errors }) => {
  const [attorneyInvolved, setAttorneyInvolved] = useState(
    data.attorneyInvolved || "No"
  );
  const [lawFirms, setLawFirms] = useState([]);
  const [lawFirmLocations, setLawFirmLocations] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loadingLawFirms, setLoadingLawFirms] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);

  const [openAttorneyDialog, setOpenAttorneyDialog] = useState(false);
  const [openLawFirmDialog, setOpenLawFirmDialog] = useState(false);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  // Initialize from `data`, preventing null overwrites
  const [lawFirm, setLawFirm] = useState(data.lawFirm || null);
  const [lawFirmAddress, setLawFirmAddress] = useState(
    data.lawFirmAddress || null
  );
  const [attorneyOfRecord, setAttorneyOfRecord] = useState(
    data.attorneyOfRecord || null
  );

  const [newAttorney, setNewAttorney] = useState({ name: "", phone: "" });
  const [newLawFirm, setNewLawFirm] = useState({ name: "", phone: "" });
  const [newAddress, setNewAddress] = useState({
    address: "",
    zip: "",
    city: "",
    county: "",
    state: "",
    phone: "",
  });
  const [fuzzyMatchDialogOpen, setFuzzyMatchDialogOpen] = useState(false);
  const [fuzzyMatchResult, setFuzzyMatchResult] = useState(null);

  const { role } = useSelector((state) => state.auth);
  const lawfirm = useSelector((state) => state.lawfirm);
  const pgId = useSelector((state) => state.pg.pgId);

  useEffect(() => {
    if (!role && pgId) {
      setAttorneyInvolved("No");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateData({ ...data, attorneyInvolved: attorneyInvolved });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attorneyInvolved]);

  useEffect(() => {
    fetchLocationsByLawFirm(lawFirm);
    fetchAttorneysByLawFirm(lawFirm);
    updateData({
      ...data,
      lawFirm: lawFirm,
      lawFirmAddress: lawFirmAddress,
      attorneyOfRecord: attorneyOfRecord,
      attorneyInvolved: attorneyInvolved,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawFirm, lawFirmAddress, attorneyOfRecord]);

  useEffect(() => {
    fetchLawFirms();
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attorneyInvolved]);

  const fetchLawFirms = async () => {
    setLoadingLawFirms(true);
    try {
      const response = await apiClient("/list-lawfirms/", "GET");
      const formattedLawFirms = response.map((firm) => ({
        label: firm.name,
        value: firm.lawfirm_id,
      }));
      setLawFirms(formattedLawFirms);
      if (
        (role === "law_firm_user" || role === "law_firm_admin") &&
        attorneyInvolved === "Yes"
      ) {
        setAttorneyInvolved("Yes");
        setLawFirm(lawfirm.lawfirmId); // Sets selected law firm
        setLawFirmAddress(lawfirm.locationId); // Sets selected address (location)
        setAttorneyOfRecord(lawfirm.lawfirmUserId); // Sets attorney for this law firm
      }
    } catch (error) {
      console.error("Error fetching law firms:", error);
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const fetchLocationsByLawFirm = async (lawFirmId) => {
    setLoadingLocations(true);
    try {
      const response = await apiClient(
        `/get-locations-by-lawfirm/${lawFirmId}`,
        "GET"
      );
      const formattedLocations = response.map((location) => ({
        label: location.name + " - " + location.address,
        value: location.law_firm_location_id,
        address: location.address,
      }));
      setLawFirmLocations(formattedLocations);
      if (data.lawFirmAddress) {
        return;
      } else if (formattedLocations.length === 1) {
        // If there is only one attorney, auto-select it
        const selectedLawFirm = formattedLocations[0];
        if (selectedLawFirm) {
          setLawFirmAddress(selectedLawFirm.value);
        }
      } else if (formattedLocations.length > 1) {
        // If there are multiple attorneys, auto-select the first one marked as `is_default: true
        const mainLocation = formattedLocations.find(
          (location) => location.label === "Main Location"
        );
        if (mainLocation) {
          setLawFirmAddress(mainLocation.value);
        }
      }
    } catch (error) {
      console.error("Error fetching law firm locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchAttorneysByLawFirm = async (lawFirmId) => {
    setLoadingAttorneys(true);
    try {
      const response = await apiClient(
        `/lawfirm-user-location/${lawFirmId}`,
        "GET"
      );
      const filteredAttorneys = response
        .filter((user) => user.Position === "Attorney")
        .map((attorney) => ({
          label: attorney.lawfirm_user_name,
          value: attorney.lawfirm_user_id,
          is_default: attorney.is_default,
        }));
      setAttorneys(filteredAttorneys);
      // Auto-select logic
      if (data.attorneyOfRecord) {
        return;
      } else if (filteredAttorneys.length === 1) {
        // If there is only one attorney, auto-select it
        const selectedAttorney = filteredAttorneys[0];
        if (selectedAttorney) {
          setAttorneyOfRecord(selectedAttorney.value);
        }
      } else if (filteredAttorneys.length > 1) {
        // If there are multiple attorneys, auto-select the first one marked as `is_default: true`
        const defaultAttorney = filteredAttorneys.find(
          (attorney) => attorney.is_default
        );
        if (defaultAttorney) {
          setAttorneyOfRecord(defaultAttorney.value);
        }
      }
    } catch (error) {
      console.error("Error fetching attorneys:", error);
    } finally {
      setLoadingAttorneys(false);
    }
  };

  const handleLawFirmChange = (event, newValue) => {
    if (newValue) {
      setLawFirm(newValue.value);
      fetchLocationsByLawFirm(newValue.value);
      fetchAttorneysByLawFirm(newValue.value);
      updateData({
        ...data,
        lawFirm: lawFirm,
        lawFirmAddress: lawFirmAddress,
        attorneyOfRecord: attorneyOfRecord,
        attorneyInvolved: attorneyInvolved,
      });
    } else {
      updateData({
        ...data,
        lawFirm: null,
        lawFirmAddress: null,
        attorneyOfRecord: null,
      });
      setLawFirmLocations([]);
      setAttorneys([]);
    }
  };

  const handleOpenAttorneyDialog = () => setOpenAttorneyDialog(true);
  const handleCloseAttorneyDialog = () => setOpenAttorneyDialog(false);
  const handleOpenLawFirmDialog = () => setOpenLawFirmDialog(true);
  const handleCloseLawFirmDialog = () => setOpenLawFirmDialog(false);
  const handleOpenAddressDialog = () => setOpenAddressDialog(true);
  const handleCloseAddressDialog = () => setOpenAddressDialog(false);

  const handleAddAttorney = async () => {
    try {
      setLoadingAttorneys(true);
      const response = await apiClient("/invite-lawfirm-user", "POST", {
        user: {
          email: `${newAttorney.name.toLowerCase().replaceAll(" ", "")}@test.com`,
        },
        law_firm_location_id: data.lawFirmAddress,
        lawfirm_id: data.lawFirm,
        lawfirm_user_name: newAttorney.name,
        preferred_name: newAttorney.name,
        title: "Attorney",
        position: "Attorney",
        is_legal_service: false,
        is_admin: false,
        middle_name: newAttorney.name,
        phone: newAttorney.phone,
        is_active: true,
      });
      updateData({ ...data, attorneyOfRecord: response.lawfirm_user_id });
      toast.success("Attorney added successfully!");
      handleCloseAttorneyDialog();
      fetchAttorneysByLawFirm(data.lawFirm); // Re-fetch attorneys and select the new one
    } catch (error) {
      console.error("Error adding attorney:", error);
      toast.error("Failed to add attorney!");
    } finally {
      setLoadingAttorneys(false);
    }
  };

  const handleAddLawFirm = async () => {
    try {
      setLoadingLawFirms(true);
      const fuzzyRes = await apiClient("/fuzzy-match-lawfirm", "POST", {
        name: newLawFirm.name,
      });
      const matchedId = fuzzyRes?.match?.lawfirm_id ?? fuzzyRes?.law_firm?.id ?? fuzzyRes?.lawfirm_id;
      const matchedName = fuzzyRes?.match?.name ?? fuzzyRes?.law_firm?.name ?? fuzzyRes?.law_firm_name ?? "";
      const hasMatch = (fuzzyRes?.match != null && matchedId != null) || (fuzzyRes?.matched && matchedId != null);
      if (hasMatch) {
        setLoadingLawFirms(false);
        setFuzzyMatchResult({ id: matchedId, name: matchedName || newLawFirm.name });
        setFuzzyMatchDialogOpen(true);
        return;
      }
      const response = await apiClient("/create-a-lawfirm", "POST", {
        name: newLawFirm.name,
        website: "",
        logo: "",
        accounting_contact_full_name: "",
        accounting_contact_email: "",
        accounting_contact_phone: "",
        is_new: true,
        is_active: true,
        created_by_guest: true,
      });
      updateData({ ...data, lawFirm: response.lawfirm_id });
      toast.success("Law Firm added successfully!");
      handleCloseLawFirmDialog();
      fetchLawFirms();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const handleUseExistingLawFirm = () => {
    if (!fuzzyMatchResult) return;
    setLawFirm(fuzzyMatchResult.id);
    fetchLocationsByLawFirm(fuzzyMatchResult.id);
    fetchAttorneysByLawFirm(fuzzyMatchResult.id);
    updateData({ ...data, lawFirm: fuzzyMatchResult.id });
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    handleCloseLawFirmDialog();
    toast.success("Using existing law firm.");
  };

  const handleCreateNewLawFirmAnyway = async () => {
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    try {
      setLoadingLawFirms(true);
      const response = await apiClient("/create-a-lawfirm", "POST", {
        name: newLawFirm.name,
        website: "",
        logo: "",
        accounting_contact_full_name: "",
        accounting_contact_email: "",
        accounting_contact_phone: "",
        is_new: true,
        is_active: true,
        created_by_guest: true,
      });
      updateData({ ...data, lawFirm: response.lawfirm_id });
      toast.success("Law Firm added successfully!");
      handleCloseLawFirmDialog();
      fetchLawFirms();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const handleAddAddress = async () => {
    try {
      setLoadingLocations(true);
      const response = await apiClient("/lawfirm-location/", "POST", [
        {
          lawfirm_id: data.lawFirm,
          name: "Guest Location",
          address: newAddress.address,
          zip_code: newAddress.zip,
          city: newAddress.city,
          county: newAddress.county,
          state: newAddress.state,
          phone: newAddress.phone,
          is_active: true,
        },
      ]);
      updateData({ ...data, lawFirmAddress: response[0].law_firm_location_id });
      toast.success("Address added successfully!");
      handleCloseAddressDialog();
      fetchLocationsByLawFirm(data.lawFirm);
    } catch (error) {
      console.error("Error adding address:", error);
      toast.error("Failed to add address!");
    } finally {
      setLoadingLocations(false);
    }
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Attorney Information
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 4 }}>
        <Typography variant="body1">
          Will an attorney remain involved throughout the duration of the case?
        </Typography>
        <RadioGroup
          row
          name="attorneyInvolved"
          value={data.attorneyInvolved || "No"}
          onChange={(e) => setAttorneyInvolved(e.target.value)}
        >
          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="No" control={<Radio />} label="No" />
        </RadioGroup>
      </FormControl>

      {attorneyInvolved === "Yes" && (
        <Box className="grid grid-cols-1 gap-4">
          {/* Law Firm Autocomplete */}
          <Autocomplete
            options={lawFirms}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLawFirms}
            value={lawFirms.find((firm) => firm.value === data.lawFirm) || null}
            onChange={handleLawFirmChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Law Firm"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLawFirms ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenLawFirmDialog}
          >
            Law firm not in the list?
          </Typography>

          {/* Law Firm Locations */}
          <Autocomplete
            options={lawFirmLocations}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLocations}
            value={
              lawFirmLocations.find(
                (loc) => loc.value === data.lawFirmAddress
              ) || null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                lawFirmAddress: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Law Firm Address"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocations ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenAddressDialog}
          >
            Address not in the list?
          </Typography>

          {/* Attorney of Record */}
          <Autocomplete
            options={attorneys}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingAttorneys}
            value={
              attorneys.find(
                (attorney) => attorney.value === data.attorneyOfRecord
              ) || null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                attorneyOfRecord: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Attorney of Record"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAttorneys ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenAttorneyDialog}
          >
            Attorney not in the list?
          </Typography>
        </Box>
      )}

      {/* Dialogs for Adding New Attorney, Law Firm, and Address */}
      <Dialog
        open={openAttorneyDialog}
        onClose={handleCloseAttorneyDialog}
        fullWidth
      >
        <DialogTitle>Add New Attorney</DialogTitle>
        <DialogContent>
          <TextField
            label="Attorney Name"
            name="name"
            value={newAttorney.name}
            onChange={(e) =>
              setNewAttorney({ ...newAttorney, name: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Phone"
            name="phone"
            value={newAttorney.phone}
            onChange={(e) =>
              setNewAttorney({ ...newAttorney, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttorneyDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAttorney} color="primary">
            Add Attorney
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openLawFirmDialog}
        onClose={handleCloseLawFirmDialog}
        fullWidth
      >
        <DialogTitle>Add New Law Firm</DialogTitle>
        <DialogContent>
          <TextField
            label="Law Firm Name"
            name="name"
            value={newLawFirm.name}
            onChange={(e) =>
              setNewLawFirm({ ...newLawFirm, name: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Phone"
            name="phone"
            value={newLawFirm.phone}
            onChange={(e) =>
              setNewLawFirm({ ...newLawFirm, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLawFirmDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddLawFirm} color="primary">
            Add Law Firm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fuzzyMatchDialogOpen} onClose={() => { setFuzzyMatchDialogOpen(false); setFuzzyMatchResult(null); }}>
        <DialogTitle>Similar law firm found</DialogTitle>
        <DialogContent>
          <Typography>
            A similar law firm was found: <strong>{fuzzyMatchResult?.name}</strong>. Do you want to use this existing firm or create a new one?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUseExistingLawFirm} color="primary">
            Use existing
          </Button>
          <Button onClick={handleCreateNewLawFirmAnyway} color="secondary">
            Create new anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddressDialog}
        onClose={handleCloseAddressDialog}
        fullWidth
      >
        <DialogTitle>Add New Address</DialogTitle>
        <DialogContent>
          <TextField
            label="Address"
            name="address"
            value={newAddress.address}
            onChange={(e) =>
              setNewAddress({ ...newAddress, address: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="ZIP"
            name="zip"
            value={newAddress.zip}
            onChange={(e) =>
              setNewAddress({ ...newAddress, zip: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="City"
            name="city"
            value={newAddress.city}
            onChange={(e) =>
              setNewAddress({ ...newAddress, city: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="County"
            name="county"
            value={newAddress.county}
            onChange={(e) =>
              setNewAddress({ ...newAddress, county: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="State"
            name="state"
            value={newAddress.state}
            onChange={(e) =>
              setNewAddress({ ...newAddress, state: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Phone"
            name="phone"
            value={newAddress.phone}
            onChange={(e) =>
              setNewAddress({ ...newAddress, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddressDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAddress} color="primary">
            Add Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const RequestorInformationForm = ({ data, bondData, updateData, errors }) => {
  const userdetails = useSelector((state) => state.auth.user);
  const phoneNumber = useSelector((state) => state.auth);

  // Function to clean country code
  const cleanPhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.replace(/^\+1/, "").replace(/\D/g, ""); // Removes "+1" and non-numeric characters
  };

  useEffect(() => {
    if (userdetails?.email) {
      updateData({
        ...data,
        email: userdetails.email,
        firstNameReq: userdetails.first_name,
        lastNameReq: userdetails.last_name,
        phoneNumber:
          data.phoneNumber || cleanPhoneNumber(phoneNumber.phoneNumber),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateData({ ...data, [name]: value });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0", // Light border for sleekness
      }}
    >
      {/* Heading */}
      <Typography variant="h5" className="font-bold mb-4">
        Requestor Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        <TextField
          label="First Name"
          name="firstNameReq"
          value={data.firstNameReq || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.firstNameReq}
          helperText={errors.firstNameReq}
        />

        <TextField
          label="Last Name"
          name="lastNameReq"
          value={data.lastNameReq || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.lastNameReq}
          helperText={errors.lastNameReq}
        />

        <TextField
          label="Email"
          name="email"
          value={data.email || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.email}
          helperText={errors.email}
        />

        <InputMask
          mask="(999) 999-9999"
          value={data.phoneNumber || ""}
          onChange={handleChange}
        >
          {(inputProps) => (
            <TextField
              {...inputProps}
              label="Phone Number"
              name="phoneNumber"
              fullWidth
              required
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
            />
          )}
        </InputMask>
      </Box>
    </Box>
  );
};

const OtherDetailsForm = ({ data, updateData, errors }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateData({ ...data, [name]: value });
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Other Details
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        <TextField
          label="Effective Date"
          name="effectiveDate"
          type="date"
          value={data.effectiveDate || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.effectiveDate}
          helperText={errors.effectiveDate}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Box>
    </Box>
  );
};

const BondNumberSection = ({ data, updateData, errors }) => (
  <Box
    className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
    sx={{ border: "1px solid #e0e0e0" }}
  >
    <Typography variant="h5" className="font-bold mb-4">
      Bond Number
    </Typography>
    <BondNumberSelect data={data} updateData={updateData} />
    {errors.bond_number && (
      <Typography color="error" variant="body2">
        {errors.bond_number}
      </Typography>
    )}
  </Box>
);

const VABondCreateHistoric = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const navigate = useNavigate();

  const [globalState, setGlobalState] = useState({
    basicInformation: {},
    generalInformation: {},
    guardianDetails: { guardians: [] },
    attorneyInformation: {},
    obligeeInformation: {},
    requestorInformation: {},
    documentUpload: {},
    billingInformation: {},
    otherDetails: {}, // Add new section
  });

  const userdetails = useSelector((state) => state.auth.user);
  const lawfirmdetails = useSelector((state) => state.lawfirm);
  const { role } = useSelector((state) => state.auth);

  // Define required fields for each section
  const requiredFieldsPerStep = {
    basicInformation: [
      "firstName",
      "lastName",
      "dateOfBirth",
      "bondAmount",
      "PrincipalNumber",
      ...(globalState.basicInformation?.hasCourtCaseNumber === "Yes" ? ["courtCaseNumber"] : []),
    ],
    generalInformation: [],
    guardianDetails: [],
    attorneyInformation: [],
    obligeeInformation: ["obligeeId"],
    requestorInformation: [
      "firstNameReq",
      "lastNameReq",
      "email",
      "phoneNumber",
    ],
    documentUpload: [],
    billingInformation: ["billing_entity"],
    otherDetails: ["effectiveDate", "bond_number"], // Add required field
  };

  const generatePayload = (globalState) => {
    const raw = globalState.otherDetails?.effectiveDate;
    const effectiveDate = raw
      ? (/^\d{4}-\d{2}-\d{2}$/.test(String(raw).trim())
          ? String(raw).trim() + "T12:00:00.000Z"
          : new Date(raw).toISOString())
      : new Date().toISOString();

    return {
      bond_type: 6,
      bond_sub_category: "None",
      bond_amount: globalState.basicInformation?.bondAmount,
      premium: globalState.basicInformation?.premiumAmount,
      court_case_number: globalState.basicInformation?.hasCourtCaseNumber === "Yes" ? globalState.basicInformation?.courtCaseNumber : "",
      state: 6,
      bond_carrier: is_prod ? 7 : 4,
      county: globalState.basicInformation?.county,
      status: "Approved",
      created_at: effectiveDate,
      updated_at: effectiveDate,
      bond_number: globalState.otherDetails?.bond_number?.bondno_id || "",
      law_firm: globalState.attorneyInformation?.lawFirm,
      law_firm_location: globalState.attorneyInformation?.lawFirmAddress,
      law_firm_user: globalState.attorneyInformation?.attorneyOfRecord,
      obligee: globalState.obligeeInformation?.obligeeId,
      requester_id: userdetails.id,
      managing_lawfirm:
        lawfirmdetails.lawfirmId || globalState.attorneyInformation?.lawFirm,
      requester_user_email: globalState.requestorInformation?.email,
      requester_phone_number: globalState.requestorInformation?.phoneNumber,
      name:
        globalState.basicInformation?.firstName +
        " " +
        globalState.basicInformation?.lastName,
      requester_name:
        globalState.requestorInformation?.firstNameReq +
        " " +
        globalState.requestorInformation?.lastNameReq,
      vabond: {
        beneficiary_legal_name: [
          globalState.basicInformation?.firstName,
          globalState.basicInformation?.middleName,
          globalState.basicInformation?.lastName,
          globalState.basicInformation?.suffix,
        ]
          .filter(Boolean)
          .join(" "),
        beneficiary_date_of_birth: globalState.basicInformation?.dateOfBirth,
        number_of_principals: globalState.basicInformation?.PrincipalNumber,
        principals_all_us_canadian_uk_citizens: true,
        fiduciary_operate_beneficiarys_business:
          globalState.generalInformation?.ongoingBusiness === "Yes",
        fiduciary_business_explanation:
          globalState.generalInformation?.explanations?.ongoingBusiness,
        principal_indebted_to_estate:
          globalState.generalInformation?.indebtedGuardians === "Yes",
        principal_indebted_explanation:
          globalState.generalInformation?.explanations?.indebtedGuardians,
        beneficiary_execute_last_will_and_testament:
          globalState.generalInformation?.didLastWillAndTestament === "Yes",
        copy_of_last_will_and_testament:
          globalState.documentUpload?.lastWillFileName,
        copy_of_last_will_and_testament_link:
          globalState.documentUpload?.lastWillLink,
        principal_replace_prior_fiduciary:
          globalState.generalInformation?.replacingFiduciary === "Yes",
        bond_replace_prior_bond:
          globalState.generalInformation?.replacingPriorBond === "Yes",
        replaced_by_non_court:
          globalState.generalInformation?.demandedByInterestedParty === "Yes",
        replaced_non_court_explanation:
          globalState.generalInformation?.explanations
            ?.demandedByInterestedParty,
        joint_control_exercised:
          globalState.generalInformation?.jointControlExercised === "Yes",
        supplemental_needs_trust_created:
          globalState.generalInformation?.supplementalNeedsTrust === "Yes",
        copy_of_supplemental_needs_trust:
          globalState.documentUpload?.otherDocumentFileName,
        copy_of_supplemental_needs_trust_link:
          globalState.documentUpload?.otherDocumentLink,
        beneficiary_first_name: globalState.basicInformation?.firstName,
        beneficiary_middle_name: globalState.basicInformation?.middleName,
        beneficiary_last_name: globalState.basicInformation?.lastName,
        beneficiary_suffix: globalState.basicInformation?.suffix,
        beneficiary_alias: globalState.basicInformation?.aliases,
        beneficiary_execute_last_will_and_testament_explaination:
          globalState.generalInformation?.explanations?.didLastWillAndTestament,
        principal_replace_prior_fiduciary_explaination:
          globalState.generalInformation?.explanations?.replacingFiduciary,
        bond_replace_prior_bond_explaination:
          globalState.generalInformation?.explanations?.replacingPriorBond,
        required_non_court_explanation:
          globalState.generalInformation?.explanations
            ?.demandedByInterestedParty,
        joint_control_explanation:
          globalState.generalInformation?.explanations?.jointControlExercised,
        supplemental_needs_trust_explanation:
          globalState.generalInformation?.explanations?.supplementalNeedsTrust,
        is_bankrupt_insolvent:
          globalState.generalInformation?.bankruptOrInsolvent === "Yes",
        is_bankrupt_insolvent_explanation:
          globalState.generalInformation?.explanations
            ?.bankruptOrInsolventExplanation,
        is_lawsuits_judgements_liens:
          globalState.generalInformation?.outstandingLawsuit === "Yes",
        is_lawsuits_judgements_liens_explanation:
          globalState.generalInformation?.explanations
            ?.outstandingLawsuitExplanation,
        copy_of_court_order: globalState.documentUpload?.courtOrderFileName,
        copy_of_court_order_link: globalState.documentUpload?.courtOrderLink,
        other_document: globalState.documentUpload?.otherDocumentFileName,
        other_document_link: globalState.documentUpload?.otherDocumentLink,
        notes: globalState.documentUpload?.note,
        billing_entity: globalState.billingInformation?.billing_entity,
        billing_lawfirmaddress_name:
          globalState.billingInformation?.billing_lawfirmaddress_name,
        billing_principal_name:
          globalState.billingInformation?.billing_principal_name,
        billing_other_name: globalState.billingInformation?.billing_other_name,
        billing_other_address:
          globalState.billingInformation?.billing_other_address,
        billing_other_relationship:
          globalState.billingInformation?.billing_other_relationship,
        billing_other_city:
          globalState.billingInformation?.billing_other_city || "",
        billing_other_state:
          globalState.billingInformation?.billing_other_state || "",
        billing_other_zip:
          globalState.billingInformation?.billing_other_zip || "",
        credit_report_allowed:
          globalState.generalInformation?.requestForAuthorization === "Yes",
        is_declined: globalState.generalInformation?.isDeclined === "Yes",
        is_declined_explanation:
          globalState.generalInformation?.explanations?.isDeclined,
      },

      principals: globalState.guardianDetails?.guardians || [],
    };
  };

  const handleFinish = async () => {
    setIsLoading(true);
    const payload = generatePayload(globalState);

    try {
      // Create bond
      await apiClient(
        "/create_va_bond_historic/",
        "POST",
        payload
      );
      setIsLoading(false);

      // Show success toast
      toast.success("Bond created successfully!");

      // Reset form state
      setGlobalState({
        basicInformation: {},
        generalInformation: {},
        guardianDetails: { guardians: [] },
        attorneyInformation: {},
        requestorInformation: {},
        documentUpload: {},
        billingInformation: {},
        otherDetails: {}, // Reset otherDetails
      });
      setErrors({}); // Clear any existing errors

      // Redirect based on user role
      const rolePathMap = {
        super_user: "/admin_home",
        internal_user: "/internal_user_home",
        law_firm_user: "/law_firm_user_home",
        law_firm_admin: "/law_firm_admin_home",
        carrier_team_member: "/carrier_team_member_dashboard",
        pg_contact: "/pg_contact_home",
      };
      navigate(rolePathMap[role]);
    } catch (error) {
      setIsLoading(false);
      const message = extractErrorMessage(error);
      console.error("Error submitting or approving the bond:", error);
      setSubmissionError(message);
      setSupportModalOpen(true);
    }
  };

  const handleSupportRequestSubmit = async (comment) => {
    try {
      const hydrationPayload = { state: globalState };
      await createSupportRequest({
        request_body: JSON.stringify(hydrationPayload),
        bond_type: "VA (Historic)",
        name: [
          globalState?.basicInformation?.firstName,
          globalState?.basicInformation?.lastName,
        ].filter(Boolean).join(" "),
        comment,
      });
      toast.success("Support request created successfully.");
      setSupportModalOpen(false);
    } catch (err) {
      console.error("Failed to create support request:", err);
      toast.error("Failed to create support request.");
    }
  };


  const updateGlobalState = (stepKey, stepData) => {
    setGlobalState((prevState) => ({
      ...prevState,
      [stepKey]:
        stepKey === "guardianDetails"
          ? {
              ...stepData,
              guardians: Array.isArray(stepData.guardians)
                ? stepData.guardians
                : [],
            }
          : stepData,
    }));
    // Clear errors for only the fields that exist in the stepData
    Object.keys(stepData).forEach((fieldName) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "",
      }));
    });
  };

  const handleSubmit = async () => {
    const allErrors = {};
    Object.keys(globalState).forEach((section) => {
      const sectionErrors = validateStepData(globalState[section], section);
      Object.assign(allErrors, sectionErrors);
    });

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);

      const lengthErrors = Object.entries(allErrors)
        .filter(([, msg]) => msg.includes("characters or less"))
        .map(([, msg]) => msg);
      const requiredErrors = Object.entries(allErrors)
        .filter(([, msg]) => !msg.includes("characters or less"));

      if (lengthErrors.length > 0) {
        lengthErrors.forEach((msg) => toast.warning(msg));
      }

      if (requiredErrors.length > 0) {
        const errorFields = requiredErrors
          .map(([field]) => {
            if (field.startsWith("guardians.")) {
              const parts = field.split(".");
              const guardianIndex = parseInt(parts[1]) + 1;
              const fieldName = parts[2];
              const fieldLabels = {
                name: "Name",
                address: "Address",
                zip_code: "ZIP Code",
                city: "City",
                state: "State",
              };
              return `Principal ${guardianIndex} ${
                fieldLabels[fieldName] || fieldName
              }`;
            }
            return field
              .replace(/([A-Z])/g, " $1")
              .replace(/\./g, " ")
              .replace(/^\w/, (c) => c.toUpperCase());
          })
          .join(", ");
        toast.error(`Please complete the following fields: ${errorFields}`);
      }
      return;
    }

    await handleFinish();
  };

  const SECTION_LIMITS_MAP = {
    basicInformation: VA_FIELD_LIMITS.basicInformation,
    generalInformation: VA_FIELD_LIMITS.generalInformation,
    guardianDetails: VA_FIELD_LIMITS.principalInformation,
    requestorInformation: VA_FIELD_LIMITS.requestorInformation,
    billingInformation: VA_FIELD_LIMITS.billingInformation,
  };

  const validateStepData = (data, section) => {
    let requiredFields = [...(requiredFieldsPerStep[section] || [])];

    if (section === "guardianDetails") {
      const count = globalState.basicInformation?.PrincipalNumber || 1;
      for (let i = 0; i < count; i++) {
        guardianFieldNames.forEach((field) => {
          requiredFields.push(`guardians.${i}.${field}`);
        });
      }
    }

    if (section === "billingInformation" && data?.billing_entity === "Other") {
      requiredFields.push(
        "billing_other_name",
        "billing_other_address",
        "billing_other_city",
        "billing_other_state",
        "billing_other_zip"
      );
    }

    const errors = {};
    requiredFields.forEach((path) => {
      const parts = path.split(".");
      let cur = data;
      for (let part of parts) {
        if (cur == null || cur[part] === undefined || cur[part] === "") {
          errors[path] = "This field is required";
          break;
        }
        cur = cur[part];
      }
    });

    const lengthErrors = validateFieldLengths(data, SECTION_LIMITS_MAP[section]);
    Object.assign(errors, lengthErrors);

    return errors;
  };

  // the exact keys your GuardianForm uses for each guardian
  const guardianFieldNames = ["name", "address", "zip_code", "city", "state"];

  return (
    <>
      <Navbar />
      <Box className="p-6 max-w-4xl mx-auto">
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          VA Bond Request for State of{" "}
          {JSON.parse(localStorage.getItem("selectedState")).label}
        </Typography>

        <Box className="space-y-8">
          <BasicInformationForm
            globalData={globalState}
            data={globalState.basicInformation}
            updateData={(data) => updateGlobalState("basicInformation", data)}
            errors={errors}
            setErrors={setErrors}
          />

          <GuardianForm
            globalData={globalState}
            data={globalState.guardianDetails}
            updateData={(data) => updateGlobalState("guardianDetails", data)}
            errors={errors}
            setErrors={setErrors}
          />

          <AttorneyInformationForm
            globalData={globalState}
            data={globalState.attorneyInformation}
            updateData={(data) =>
              updateGlobalState("attorneyInformation", data)
            }
            errors={errors}
            setErrors={setErrors}
          />

          <ObligeeInformationForm
            globalData={globalState}
            data={globalState.obligeeInformation}
            updateData={(data) => updateGlobalState("obligeeInformation", data)}
            errors={errors}
            setErrors={setErrors}
            bondType="VA"
          />

          <RequestorInformationForm
            globalData={globalState}
            data={globalState.requestorInformation}
            updateData={(data) =>
              updateGlobalState("requestorInformation", data)
            }
            errors={errors}
            setErrors={setErrors}
          />

          <BillingInformationForm
            globalData={globalState}
            data={globalState.billingInformation}
            updateData={(data) => updateGlobalState("billingInformation", data)}
            errors={errors}
            setErrors={setErrors}
          />

          <OtherDetailsForm
            globalData={globalState}
            data={globalState.otherDetails}
            updateData={(data) => updateGlobalState("otherDetails", data)}
            errors={errors}
            setErrors={setErrors}
          />

          <BondNumberSection
            data={globalState.otherDetails}
            updateData={(data) => updateGlobalState("otherDetails", data)}
            errors={errors}
          />

          <Box className="flex justify-end mt-8">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={globalState?.basicInformation?.judgeStern === "No"}
              endIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? "Processing..." : "Submit"}
            </Button>
          </Box>
        </Box>
      </Box>
      <SupportRequestModal
        open={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        onSubmit={handleSupportRequestSubmit}
        errorMessage={submissionError}
      />
    </>
  );
};

export default VABondCreateHistoric;
